/**
 * F1 (paridad Ledgeria) — SUMAS Y SALDOS (balance de comprobación).
 *
 * Por cada cuenta: debe, haber y saldo del periodo, agrupable por nivel PGC
 * (grupo / subgrupo / cuenta / subcuenta). El aviso de cuadre es lo que hace
 * útil el informe: si debe ≠ haber, el libro está descuadrado.
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, CheckCircle2, Loader2, Scale } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../lib/api'
import { formatCents } from '../lib/money'
import type { TrialBalanceResponse } from '@toga/shared'

const inputCls = 'rounded-md border px-2 py-1.5 text-sm'
const inputStyle = { background: 'var(--bg-input)', borderColor: 'var(--border)' } as const

const LEVELS = [
  { value: '1', key: 'group' },
  { value: '2', key: 'subgroup' },
  { value: '3', key: 'account' },
  { value: '', key: 'detail' },
] as const

export default function AccountingTrialBalance() {
  const { t } = useTranslation(['accounting', 'common'])
  const currentYear = new Date().getFullYear()

  const [year, setYear] = useState(String(currentYear))
  const [level, setLevel] = useState<string>('3')
  const [hideZero, setHideZero] = useState(true)

  const [data, setData] = useState<TrialBalanceResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ hideZero: String(hideZero) })
      if (year) params.set('year', year)
      if (level) params.set('level', level)
      setData(await api.get<TrialBalanceResponse>(`/accounting/reports/trial-balance?${params}`))
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('trialBalance.loadError')))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [year, level, hideZero, t])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-serif font-semibold t-primary flex items-center gap-2">
          <Scale size={22} className="text-toga-700 dark:text-toga-300" />
          {t('trialBalance.title')}
        </h1>
        <p className="t-muted text-sm mt-1">{t('trialBalance.subtitle')}</p>
      </header>

      <div className="surface p-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs t-muted">{t('trialBalance.year')}</span>
          <input
            value={year}
            onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
            className={`${inputCls} w-24`}
            style={inputStyle}
            inputMode="numeric"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs t-muted">{t('trialBalance.level')}</span>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className={`${inputCls} min-w-[12rem]`}
            style={inputStyle}
          >
            {LEVELS.map((l) => (
              <option key={l.key} value={l.value}>
                {t(`trialBalance.levels.${l.key}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm t-secondary pb-1.5">
          <input
            type="checkbox"
            checked={hideZero}
            onChange={(e) => setHideZero(e.target.checked)}
            className="h-4 w-4 accent-toga-600"
          />
          {t('trialBalance.hideZero')}
        </label>
      </div>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {loading && (
        <div className="surface p-8 text-center t-muted text-sm">
          <Loader2 size={18} className="inline animate-spin mr-2" /> {t('common:loading')}
        </div>
      )}

      {data && !loading && (
        <>
          <div
            className={`text-sm rounded-lg px-3 py-2 border flex items-center gap-2 ${
              data.balanced
                ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
                : 'text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30'
            }`}
          >
            {data.balanced ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
            {data.balanced
              ? t('trialBalance.balanced')
              : t('trialBalance.unbalanced', {
                  diff: formatCents(data.totalDebitCents - data.totalCreditCents),
                })}
          </div>

          <div className="surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
                  <th className="px-3 py-2 font-medium">{t('trialBalance.col.code')}</th>
                  <th className="px-3 py-2 font-medium">{t('trialBalance.col.name')}</th>
                  <th className="px-3 py-2 font-medium text-right">{t('trialBalance.col.debit')}</th>
                  <th className="px-3 py-2 font-medium text-right">{t('trialBalance.col.credit')}</th>
                  <th className="px-3 py-2 font-medium text-right">{t('trialBalance.col.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center t-muted text-sm">
                      {t('trialBalance.empty')}
                    </td>
                  </tr>
                ) : (
                  data.rows.map((r) => (
                    <tr key={r.code} className="border-b border-base/40 last:border-b-0 hover:bg-hover/40">
                      <td className="px-3 py-2 font-mono text-xs t-primary">{r.code}</td>
                      <td className="px-3 py-2 t-secondary">{r.name}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums t-secondary">
                        {r.debitCents ? formatCents(r.debitCents) : ''}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums t-secondary">
                        {r.creditCents ? formatCents(r.creditCents) : ''}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono tabular-nums ${
                          r.balanceCents < 0 ? 'text-amber-700 dark:text-amber-300' : 't-primary'
                        }`}
                      >
                        {r.balanceCents < 0
                          ? `(${formatCents(Math.abs(r.balanceCents))})`
                          : formatCents(r.balanceCents)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-base font-semibold">
                  <td className="px-3 py-2" colSpan={2}>
                    {t('trialBalance.total')}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums t-primary">
                    {formatCents(data.totalDebitCents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums t-primary">
                    {formatCents(data.totalCreditCents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums t-primary">
                    {formatCents(data.totalDebitCents - data.totalCreditCents)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
